import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./OddLetter.css";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "M", "N", "O", "P", "Q", "R", "S", "T", "W", "Z"];

const SIMILAR_PAIRS = [
  ["O", "Q"],
  ["E", "F"],
  ["M", "N"],
  ["P", "R"],
  ["C", "G"],
  ["I", "J"],
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 3,
    columns: 3,
    mode: "different",
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 6,
    columns: 3,
    mode: "different",
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 9,
    columns: 3,
    mode: "similar",
  },
};

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  return [...list].sort(() => Math.random() - 0.5);
}

function createRound(config) {
  let base;
  let odd;

  if (config.mode === "similar") {
    const pair = randomItem(SIMILAR_PAIRS);
    base = pair[0];
    odd = pair[1];
  } else {
    const selected = shuffle(LETTERS).slice(0, 2);
    base = selected[0];
    odd = selected[1];
  }

  const oddIndex = Math.floor(Math.random() * config.itemCount);

  const items = Array.from({ length: config.itemCount }, (_, index) => ({
    id: index,
    letter: index === oddIndex ? odd : base,
    isOdd: index === oddIndex,
  }));

  return {
    base,
    odd,
    oddIndex,
    items,
  };
}

export default function OddLetter() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStarted(true);
    setFinished(false);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound(nextConfig));
  };

  const answer = (item) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = item.isOdd;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: item.letter,
        correctAnswer: round.odd,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) setCorrect((prev) => prev + 1);
    else setErrors((prev) => prev + 1);

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Letra diferente"
        description="Observa la secuencia de letras y pulsa la letra que sea diferente a las demás."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Discriminación visual",
          "Lectoescritura",
        ]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="odd-letter-page">
      <div className="odd-letter-card">
        <header className="odd-letter-header">
          <div>
            <h1>Letra diferente</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="odd-letter-instruction">
              <h2>¿Qué letra es diferente?</h2>
            </section>

            <section
              className="odd-letter-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="odd-letter-item"
                  onClick={() => answer(item)}
                >
                  {item.letter}
                </button>
              ))}
            </section>

            <section className="odd-letter-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}